import { message } from "antd";
import {
  IChangeApplicationBankOnlyStatusResponse,
  IStandartErrorResponse,
} from "../../../interfaces/store/api/APIInterfaces";
import {
  IApplicationDataToBank,
  IApplicationStatus,
} from "../../slice/applicationDataToBankSlice/applicationDataToBankSlice";
import { api } from "../apiCore";
import { isFetchBaseQueryError } from "../typeGuards/typeGuards";
import {
  IChangeApplicationBankStatusErrorResponse,
  IChangeApplicationDataToBank,
  SetApplicationBankStatusResponse,
} from "../types/applicationBankApiTypes";
import {
  setApplicationBankStatusResponseMessage,
  SetApplicationStatusAction,
} from "./config/config";

// запросы для работы с заявкой для банка
export const applicationDataToBankApi = api.injectEndpoints({
  endpoints: (builder) => ({
    //** получение данных дополнительной заявки */
    getApplicationDataToBank: builder.query<
      IApplicationDataToBank,
      { applicationId: number; applicationBankId: number }
    >({
      query: ({ applicationId, applicationBankId }) => ({
        url: `/auth/applications/system-bank/create-data-to-bank/${applicationId}/application-bank/${applicationBankId}`,
        method: "POST",
      }),
      providesTags: (result, error, { applicationBankId }) => [
        { type: "ApplicationBank", applicationBankId },
      ],
      transformResponse: (response: { data: IApplicationDataToBank }) =>
        response.data,
    }),
    //** изменение данных дополнительной заявки */
    changeApplicationDataToBank: builder.mutation<
      IApplicationDataToBank,
      IChangeApplicationDataToBank
    >({
      query: (data) => ({
        url: `/auth/applications/system-bank/create-data-to-bank/${data.applicationId}/application-bank/${data.applicationBankId}`,
        method: "PUT",
        body: {
          contactApplication: data.contactApplication,
          bankRequisites: data.bankRequisites,
          accountingBalance: data.accountingBalance,
        },
      }),
      async onQueryStarted(
        { applicationBankId, applicationId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedApplicationBank } = await queryFulfilled;

          dispatch(
            applicationDataToBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              { applicationId, applicationBankId },
              (draft) => {
                Object.assign(draft, updatedApplicationBank);
              },
            ),
          );
          message.success("Заявка сохранена успешно.");
        } catch {
          message.error("Не удалось сохранить заявку.");
        }
      },

      transformResponse: (response: { data: IApplicationDataToBank }) =>
        response.data,
    }),

    /* Для обновления статусов доп. заявки по эндпоинтам. В config писать новые ключевые слова для новых статусов*/
    setApplicationBankStatus: builder.mutation<
      IApplicationDataToBank,
      {
        applicationId: number;
        applicationBankId: number;
        action: SetApplicationStatusAction;
      }
    >({
      query: ({ applicationBankId, action }) => {
        return {
          url: `auth/applications/system-bank/create-data-to-bank/${applicationBankId}/processing/${action}`,
          method: "POST",
        };
      },
      transformResponse: (response: {
        message: IApplicationDataToBank;
        success?: boolean;
      }) => response.message,
      async onQueryStarted(
        { applicationBankId, applicationId, action },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedApplicationBank } = await queryFulfilled;

          dispatch(
            applicationDataToBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              { applicationBankId, applicationId },
              (draft) => {
                Object.assign(draft, updatedApplicationBank);
              },
            ),
          );
          message.success(
            setApplicationBankStatusResponseMessage[action].success,
          );
        } catch {
          message.error(setApplicationBankStatusResponseMessage[action].error);
        }
      },
      invalidatesTags: ["ApplicationBank"],
    }),

    /* Для обновления статусов доп. заявки по эндпоинтам. В config писать новые ключевые слова для новых статусов
    (если сервер присылает инфу только о новом статусе заявки) */
    setApplicationBankStatusOnlyNewInfoStatus: builder.mutation<
      IChangeApplicationBankOnlyStatusResponse["message"],
      {
        applicationId: number;
        applicationBankId: number;
        action: SetApplicationBankStatusResponse;
      }
    >({
      query: ({ applicationBankId, action }) => {
        return {
          url: `auth/applications/system-bank/create-data-to-bank/${applicationBankId}/processing/${action}`,
          method: "POST",
        };
      },
      transformResponse: (response: IChangeApplicationBankOnlyStatusResponse) =>
        response.message,
      transformErrorResponse: (
        error: IStandartErrorResponse<IChangeApplicationBankStatusErrorResponse>,
      ) => {
        if (
          typeof error.status === "number" &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return new Error(error.data?.message);
        }

        if (typeof error.status === "number" && error.status >= 500) {
          return new Error("Ошибка сервера");
        }

        if (isFetchBaseQueryError(error)) {
          return new Error("Ошибка подключения к серверу");
        }

        return undefined;
      },
      async onQueryStarted(
        { applicationBankId, applicationId, action },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedStatusApplicationBank } = await queryFulfilled;

          dispatch(
            applicationDataToBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              { applicationBankId, applicationId },
              (draft) => {
                draft.status = updatedStatusApplicationBank;

                if (
                  action ===
                  SetApplicationBankStatusResponse["return_from_reject"]
                ) {
                  draft.refusal_info = null;
                }
              },
            ),
          );
        } catch (e) {}
      },
    }),
    sendDataToSigning: builder.mutation<
      { code: string; message: IApplicationStatus },
      { applicationBankId: number | string }
    >({
      query: (value) => ({
        url: `auth/applications/system-bank/create-data-to-bank/${value.applicationBankId}/processing/document-verification`,
        method: "POST",
      }),
      invalidatesTags: ["ApplicationBank"],
    }),

    //
  }),
});

export const {
  useGetApplicationDataToBankQuery,
  useLazyGetApplicationDataToBankQuery,
  useChangeApplicationDataToBankMutation,
  useSetApplicationBankStatusMutation,
  useSetApplicationBankStatusOnlyNewInfoStatusMutation,
  useSendDataToSigningMutation,
} = applicationDataToBankApi;
